/**
 * CLR Row Expansion — Quote history panel for a commercial prospect.
 */
import React, { useState } from 'react';
import {
  FiZap, FiRefreshCw, FiCheck, FiX, FiClock,
  FiAlertTriangle, FiArrowRight, FiExternalLink, FiChevronDown, FiChevronUp,
  FiTool, FiShield,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';

export interface CLCarrierEntry {
  name: string;
  premium: string;
  premiumRaw: number | null;
  status: 'quoted' | 'bridged' | 'declined' | 'failed';
  logoURL?: string | null;
  bindLink?: string | null;
  failureReason?: string | null;
  grade?: string | null;
}

export interface CLQuoteRunEntry {
  id: string;
  executedAt: string;
  runStatus: 'completed' | 'failed' | 'running' | 'awaiting_input';
  carriers: CLCarrierEntry[];
  errorMessage?: string | null;
  durationMs?: number | null;
  rateUrl?: string | null;
  policyType?: string | null;
  lob?: string | null;
}

interface Props {
  colSpan: number;
  runs: CLQuoteRunEntry[];
  isLoading: boolean;
  onOpenWorkflow: (runId: string) => void;
  onRefresh: () => void;
  onExecute?: () => void;
  isRenewal?: boolean;
}

// ── Deterministic badge color from carrier name ──
const BADGE_PALETTES = [
  ['#6d28d9', '#4f46e5'],  // purple→indigo
  ['#0f766e', '#0891b2'],  // teal→cyan
  ['#b45309', '#d97706'],  // amber→orange
  ['#1d4ed8', '#2563eb'],  // blue
  ['#7c3aed', '#a855f7'],  // violet→purple
  ['#0369a1', '#0ea5e9'],  // sky
  ['#166534', '#15803d'],  // green
  ['#be185d', '#db2777'],  // pink
];
function badgeGradient(name: string): string {
  const idx = (name.charCodeAt(0) ?? 0) % BADGE_PALETTES.length;
  const [a, b] = BADGE_PALETTES[idx];
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
}

// ── Carrier badge (logo or initial with color) ──
const CarrierBadge: React.FC<{ name: string; logoURL?: string | null; size?: 'sm' | 'md' }> = ({
  name, logoURL, size = 'md',
}) => {
  const sz = size === 'sm' ? 'rql-clr-badge--sm' : '';
  return logoURL ? (
    <div className={`rql-clr-badge rql-clr-badge--logo ${sz}`}>
      <img src={logoURL} alt={name} className="rql-clr-badge__img" />
    </div>
  ) : (
    <div
      className={`rql-clr-badge ${sz}`}
      style={{ background: badgeGradient(name) }}
    >
      {(name[0] || '?').toUpperCase()}
    </div>
  );
};

// ── Single quoted / bridged carrier card ──
const QuotedCard: React.FC<{ c: CLCarrierEntry; isBest: boolean }> = ({ c, isBest }) => (
  <div className={[
    'rql-clr-card',
    isBest ? 'rql-clr-card--best' : '',
    c.status === 'bridged' ? 'rql-clr-card--bridge' : '',
  ].filter(Boolean).join(' ')}>

    {/* Left accent bar for best */}
    {isBest && c.status !== 'bridged' && <div className="rql-clr-card__accent" />}

    <div className="rql-clr-card__left">
      <CarrierBadge name={c.name} logoURL={c.logoURL} />
      <div className="rql-clr-card__meta">
        <span className="rql-clr-card__name">{c.name}</span>
        <div className="rql-clr-card__badges">
          {isBest && c.status !== 'bridged' && (
            <span className="rql-clr-card__best-pill"><FiCheck size={8} /> Best Price</span>
          )}
          {c.status === 'bridged' && (
            <span className="rql-clr-card__bridge-pill"><FiExternalLink size={8} /> Bridge Required</span>
          )}
          {c.grade && <span className="rql-clr-card__grade">Grade {c.grade}</span>}
        </div>
      </div>
    </div>

    <div className="rql-clr-card__right">
      <div className="rql-clr-card__pricing">
        <div className="rql-clr-card__premium">
          <span className="rql-clr-card__amount">
            {c.status === 'bridged' && c.premiumRaw ? `~${c.premium}` : c.premium}
          </span>
          <span className="rql-clr-card__freq">{c.status === 'bridged' ? '/ yr est.' : '/ yr'}</span>
        </div>
        {c.premiumRaw && (
          <span className="rql-clr-card__monthly">
            ~${Math.round(c.premiumRaw / 12).toLocaleString()}/mo
          </span>
        )}
      </div>
      {c.bindLink && (
        <a
          href={c.bindLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`rql-clr-card__bind${c.status === 'bridged' ? ' rql-clr-card__bind--bridge' : ''}`}
          onClick={e => e.stopPropagation()}
        >
          <FiExternalLink size={10} />
          {c.status === 'bridged' ? 'Bridge' : 'Bind Now'}
        </a>
      )}
    </div>
  </div>
);

// ── Declined carrier row ──
const DeclinedRow: React.FC<{ c: CLCarrierEntry }> = ({ c }) => {
  const reason = c.failureReason?.trim() || (c.status === 'failed' ? 'Failed' : 'Declined');
  const isBridged = c.status === 'bridged';
  return (
    <div className="rql-clr-declined__row">
      <CarrierBadge name={c.name} logoURL={c.logoURL} size="sm" />
      <span className="rql-clr-declined__name">{c.name}</span>
      <span className={`rql-clr-declined__status ${isBridged ? 'rql-clr-declined__status--bridge' : ''}`}>
        {isBridged ? <FiExternalLink size={9} /> : <FiX size={9} />}
        {isBridged ? 'Bridged' : c.status === 'failed' ? 'Failed' : 'Declined'}
      </span>
      {reason && !isBridged && (
        <span className="rql-clr-declined__reason" title={reason}>{reason}</span>
      )}
    </div>
  );
};

// ── Run block ──
const DECLINED_PREVIEW = 4;

const RunBlock: React.FC<{ run: CLQuoteRunEntry; index: number; onOpen: () => void; isRenewal?: boolean }> = ({
  run, index, onOpen, isRenewal,
}) => {
  const [declinedExpanded, setDeclinedExpanded] = useState(false);

  const quoted = run.carriers
    .filter(c => c.status === 'quoted' || (c.status === 'bridged' && c.premiumRaw))
    .sort((a, b) => (a.premiumRaw ?? Infinity) - (b.premiumRaw ?? Infinity));
  const declined = run.carriers.filter(
    c => c.status !== 'quoted' && !(c.status === 'bridged' && c.premiumRaw)
  );
  const best = quoted.find(c => c.status === 'quoted') ?? quoted[0] ?? null;
  const isActive = run.runStatus === 'running' || run.runStatus === 'awaiting_input';

  const clrLobRaw = (run.policyType || '').toUpperCase();
  const clrLobCls = clrLobRaw === 'CYBER' ? 'cyber' : clrLobRaw === 'WC' ? 'wc' : clrLobRaw === 'GL' ? 'gl' : clrLobRaw === 'BOP' ? 'bop' : '';
  const clrLobLabel = clrLobRaw === 'CYBER' ? 'Cyber' : clrLobRaw === 'WC' ? 'Workers\u2019 Comp' : clrLobRaw === 'GL' ? 'Gen. Liability' : clrLobRaw === 'BOP' ? 'BOP' : '';

  const visibleDeclined = declinedExpanded ? declined : declined.slice(0, DECLINED_PREVIEW);
  const hasMore = declined.length > DECLINED_PREVIEW;

  return (
    <div className={`rql-clr-run rql-clr-run--${run.runStatus}`}>

      {/* ── Run header ── */}
      <div className="rql-clr-run__hdr">
        <div className="rql-clr-run__hdr-left">
          <span className={`rql-clr-run__dot rql-clr-run__dot--${run.runStatus}`} />
          <span className="rql-clr-run__num">Quote #{index + 1}</span>
          {clrLobCls && (
            <span className={`rql-run-block__lob rql-run-block__lob--${clrLobCls}`}>
              {clrLobRaw === 'WC' ? <FiTool size={8}/> : <FiShield size={8}/>}
              {clrLobLabel}
            </span>
          )}
          <span className="rql-clr-run__sep">·</span>
          <span className="rql-clr-run__date">{run.executedAt}</span>
          {run.durationMs != null && run.durationMs > 0 && (
            <>
              <span className="rql-clr-run__sep">·</span>
              <span className="rql-clr-run__dur">
                <FiClock size={9} />
                {run.durationMs >= 60000
                  ? `${Math.floor(run.durationMs / 60000)}m ${Math.round((run.durationMs % 60000) / 1000)}s`
                  : `${(run.durationMs / 1000).toFixed(0)}s`}
              </span>
            </>
          )}
        </div>

        <div className="rql-clr-run__hdr-right">
          {/* Status pill */}
          {run.runStatus === 'completed' && (
            <span className="rql-clr-run__pill rql-clr-run__pill--ok">
              <FiCheck size={9} /> Completed
            </span>
          )}
          {run.runStatus === 'failed' && (
            <span className="rql-clr-run__pill rql-clr-run__pill--err">
              <FiX size={9} /> Failed
            </span>
          )}
          {run.runStatus === 'running' && (
            <span className="rql-clr-run__pill rql-clr-run__pill--running">
              <FiRefreshCw size={9} className="rql-row-execute__spin" /> Running
            </span>
          )}
          {run.runStatus === 'awaiting_input' && (
            <span className="rql-clr-run__pill rql-clr-run__pill--await">
              <RiSparklingFill size={9} /> Needs Input
            </span>
          )}

          {/* Carrier summary */}
          {quoted.length > 0 && (
            <span className="rql-clr-run__summary rql-clr-run__summary--quoted">
              <FiCheck size={9} /> {quoted.length} quoted
            </span>
          )}
          {declined.length > 0 && (
            <span className="rql-clr-run__summary rql-clr-run__summary--declined">
              {declined.length} declined
            </span>
          )}

          {/* Action buttons — Go to Rate for quoted/bridged/declined, hide for errors-only */}
          {(run.runStatus === 'completed' || run.runStatus === 'failed') && (
            <>
              {run.carriers.length > 0 && run.carriers.some(c => c.status !== 'failed') && (
                <button
                  type="button"
                  className="rql-clr-run__view-btn"
                  style={{ background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', borderColor: 'rgba(16,185,129,0.5)', boxShadow: '0 2px 10px rgba(16,185,129,0.30)' }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (run.rateUrl) { window.open(run.rateUrl, '_blank'); return; }
                    try {
                      const myampUrl = process.env.REACT_APP_MyAMP_Url || '';
                      const token = localStorage.getItem('authToken') || '';
                      const base = isRenewal ? 'rate-quoting' : 'commercial-rating';
                      const res = await fetch(`${myampUrl}/api/ams/${base}/runs/${run.id}/rate-url`, { headers: { Authorization: `Bearer ${token}` } });
                      if (res.ok) { const data = await res.json(); window.open(data.rate_url, '_blank'); }
                    } catch { /* silently fail */ }
                  }}
                >
                  Go to Rate <FiArrowRight size={10} />
                </button>
              )}
              <button type="button" className="rql-clr-run__view-btn" onClick={e => { e.stopPropagation(); onOpen(); }}>
                View Details <FiArrowRight size={10} />
              </button>
            </>
          )}
          {isActive && (
            <button type="button" className="rql-clr-run__view-btn rql-clr-run__view-btn--active" onClick={e => { e.stopPropagation(); onOpen(); }}>
              <RiSparklingFill size={10} /> Review &amp; Submit
            </button>
          )}
        </div>
      </div>

      {/* ── Active placeholder ── */}
      {isActive && run.carriers.length === 0 && (
        <div className="rql-clr-run__working">
          {run.runStatus === 'awaiting_input' ? (
            <>
              <RiSparklingFill size={12} style={{ color: '#a78bfa' }} />
              <span>AI paused — your input is needed to continue.</span>
            </>
          ) : (
            <>
              <FiRefreshCw size={12} className="rql-row-execute__spin" style={{ color: '#818cf8' }} />
              <span>AI agent working across carriers…</span>
            </>
          )}
        </div>
      )}

      {/* ── Error ── */}
      {run.runStatus === 'failed' && quoted.length === 0 && declined.length === 0 && run.errorMessage && (
        <div className="rql-clr-run__err">
          <FiAlertTriangle size={12} />
          <span>{run.errorMessage.length > 180 ? run.errorMessage.slice(0, 180) + '…' : run.errorMessage}</span>
        </div>
      )}

      {/* ── Quoted carrier cards ── */}
      {quoted.length > 0 && (
        <div className="rql-clr-cards">
          {quoted.map((c, i) => (
            <QuotedCard key={i} c={c} isBest={c === best} />
          ))}
        </div>
      )}

      {/* ── Declined/failed list ── */}
      {declined.length > 0 && (
        <div className="rql-clr-declined">
          <div className="rql-clr-declined__hdr">
            <div className="rql-clr-declined__label">
              <FiAlertTriangle size={10} />
              {declined.length} carrier{declined.length !== 1 ? 's' : ''} declined
            </div>
            {hasMore && (
              <button type="button" className="rql-clr-declined__toggle" onClick={e => { e.stopPropagation(); setDeclinedExpanded(v => !v); }}>
                {declinedExpanded ? <><FiChevronUp size={10} /> Show less</> : <><FiChevronDown size={10} /> Show all {declined.length}</>}
              </button>
            )}
          </div>
          <div className="rql-clr-declined__list">
            {visibleDeclined.map((c, i) => <DeclinedRow key={i} c={c} />)}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main component ──
const CLRRowExpand: React.FC<Props> = ({
  colSpan, runs, isLoading, onOpenWorkflow, onRefresh, onExecute, isRenewal,
}) => (
  <tr className="rql-expand-row">
    <td colSpan={colSpan} className="rql-expand-row__cell">
      <div className="rql-expand">

        {/* Header */}
        <div className="rql-hist-head">
          <span className="rql-hist-head__icon"><FiZap size={11} /></span>
          <span className="rql-hist-head__title">Quote History</span>
          {!isLoading && runs.length > 0 && (
            <span className="rql-hist-head__count">{runs.length}</span>
          )}
          <button
            type="button"
            className="rql-hist-head__refresh"
            onClick={e => { e.stopPropagation(); onRefresh(); }}
            disabled={isLoading}
          >
            <FiRefreshCw size={11} className={isLoading ? 'rql-row-execute__spin' : ''} />
          </button>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="rql-clr-expand-skel">
            {[80, 60, 75].map((w, i) => (
              <div key={i} className="rql-clr-expand-skel__row">
                <div className="rql-clr-expand-skel__dot" />
                <div className="rql-clr-expand-skel__bar" style={{ width: `${w}%` }} />
              </div>
            ))}
          </div>
        ) : runs.length === 0 ? (
          <div className="rql-expand__empty">
            <div className="rql-expand__empty-icon"><FiZap size={22} /></div>
            <p className="rql-expand__empty-msg">No quote runs yet</p>
            <p className="rql-expand__empty-hint">Start the AI workflow to get carrier quotes.</p>
            {onExecute && (
              <button
                type="button"
                className="rql-expand__cta"
                onClick={e => { e.stopPropagation(); onExecute(); }}
              >
                <FiZap size={11} /> Start Quote Workflow <FiArrowRight size={11} />
              </button>
            )}
          </div>
        ) : (
          <div className="rql-run-list">
            {runs.map((run, ri) => (
              <RunBlock
                key={run.id}
                run={run}
                index={ri}
                onOpen={() => onOpenWorkflow(run.id)}
                isRenewal={isRenewal}
              />
            ))}
          </div>
        )}

      </div>
    </td>
  </tr>
);

export default CLRRowExpand;
